/**
 * Custom emoji transport.
 *
 * The image is sent base64-encoded in the JSON body: the server must inspect
 * the bytes before accepting them (see apps/api/src/services/emoji.ts), so a
 * presigned direct-to-storage PUT is not available here. Emoji are capped at
 * 256 KB, which is why this is acceptable and files are not.
 */

import type { CustomEmoji, CustomEmojiListResponse } from '@cyphex/types/admin';
import { apiDelete, apiGet, apiPost } from '@/lib/api/client';
import { API_BASE_URL } from '@/lib/api/env';

export const EMOJI_MAX_BYTES = 256 * 1024;

/**
 * Resolve the `url` the API returned into something an `<img>` can load.
 *
 * With a CDN configured it is already absolute. Without one it is the SIGNED
 * `/v1/emoji/:id/content?t=&e=&s=` path — correct as-is only while the API is
 * same-origin, so rebasing it onto `API_BASE_URL` keeps split-origin
 * deployments working instead of 404ing against the web host.
 */
export function emojiSrc(url: string): string {
  if (url === '' || /^https?:\/\//i.test(url)) return url;
  return url.startsWith('/v1/') ? `${API_BASE_URL}${url.slice('/v1'.length)}` : url;
}

export const EMOJI_MIME_TYPES: readonly string[] = [
  'image/png',
  'image/gif',
  'image/webp',
  'image/jpeg',
];

export interface CreateEmojiInput {
  name: string;
  /** Raw base64, no data-url prefix. */
  data?: string;
  contentType?: string;
  aliasOf?: string;
}

/**
 * FileReader gives a `data:` URL; the API accepts either, but stripping the
 * prefix here keeps the request body ~40 bytes smaller per upload and makes the
 * wire format one thing instead of two.
 */
export function readImageAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('read_failed'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma > 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export const emojiApi = {
  list: (): Promise<CustomEmojiListResponse> => apiGet<CustomEmojiListResponse>('/emoji'),

  create: (input: CreateEmojiInput): Promise<CustomEmoji> =>
    apiPost<CustomEmoji>('/emoji', input),

  remove: (emojiId: string): Promise<void> =>
    apiDelete<{ ok: boolean }>(`/emoji/${emojiId}`).then(() => undefined),
} as const;
